import { useUser } from '@corratech/pylot-auth-manager'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import {
    CreateCardOutput,
    createCardQuery
} from '@pylot-data/api/operations/graphql/createCardQuery'
import {
    DeleteCardOutput,
    deleteCardQuery
} from '@pylot-data/api/operations/graphql/deleteCardQuery'
import {
    GetCardOutput,
    getCardQuery
} from '@pylot-data/api/operations/graphql/getCardQuery'
import { GraphQLError, graphqlFetch } from '@pylot-data/graphqlFetch'
import { useQuery } from '@pylot-data/hooks/use-query'
import { useTranslation } from 'next-i18next/pages'
import React from 'react'

type MutatorCallback<Data> = (
    currentValue: undefined | Data
) => Promise<undefined | Data> | undefined | Data

interface CardData {
    data: GetCardOutput
    errors?: GraphQLError[]
}

type CardInfoContextType = {
    numberOfInstallments: number
    setNumOfInstallments: React.Dispatch<React.SetStateAction<number>>
    installmentsArray: number[]
    isLoading: boolean
    isValidating: boolean
    data?: CardData
    cardNumber?: string | null
    cardType?: string
    cardBrand?: string
    maxInstallments: number
    error: unknown
    mutate: (
        data?: CardData | Promise<CardData> | MutatorCallback<CardData>,
        shouldRevalidate?: boolean
    ) => Promise<CardData | undefined>
    handleCreateCard: () => Promise<void>
    handleRemoveCard: () => Promise<void>
    isCreateCardLoading: boolean
    setIsCreateCardLoading: React.Dispatch<React.SetStateAction<boolean>>
}

const CardInfoContext = React.createContext<CardInfoContextType | undefined>(
    undefined
)

function CardInfoProvider({
    shouldFetch = true,
    children
}: {
    shouldFetch?: boolean
    children: React.ReactNode
}): React.ReactElement {
    const [numberOfInstallments, setNumOfInstallments] = React.useState(1)
    const [isCreateCardLoading, setIsCreateCardLoading] = React.useState(false)

    const { t } = useTranslation('account')
    const { openToast } = useUI()
    const { isSignedIn, customer } = useUser()

    const shouldLoadCard = isSignedIn && customer && shouldFetch

    const { data, isValidating, error, mutate } = useQuery<
        Record<string, never>,
        GetCardOutput
    >(shouldLoadCard ? getCardQuery : null, {}, { revalidateOnFocus: false })

    const isLoading = isValidating && !data

    const card = data?.data?.getCard
    const cardNumber = card?.cardNumber
    const cardType = card?.cardType
    const cardBrand = card?.cardBrand
    const installments = card?.installments

    const maxInstallments = React.useMemo(() => {
        const parsedValue = Number(installments?.split('-')[1])
        return Number.isNaN(parsedValue) ? 0 : parsedValue
    }, [installments])

    const installmentsArray = React.useMemo(
        () => Array.from({ length: maxInstallments }, (_, index) => index + 1),
        [maxInstallments]
    )

    const showGenericError = React.useCallback(() => {
        openToast(
            t('account|An error occured. Please try again'),
            ToastType.Danger
        )
    }, [openToast, t])

    const handleCreateCard = React.useCallback(async () => {
        setIsCreateCardLoading(true)

        try {
            const response = await graphqlFetch<
                Record<string, never>,
                CreateCardOutput
            >({
                query: createCardQuery
            })

            if (response.errors) {
                showGenericError()
                return
            }

            const { token, formkey, urlWebpay } = response.data.createCard

            window.location.assign(
                `${urlWebpay}?form_key=${formkey}&TBK_TOKEN=${token}`
            )
        } catch {
            showGenericError()
        } finally {
            setIsCreateCardLoading(false)
        }
    }, [showGenericError])

    const handleRemoveCard = React.useCallback(async () => {
        const optimisticCardData: CardData = {
            data: {
                getCard: {
                    status: true,
                    cardType: '',
                    installments: '',
                    cardNumber: null,
                    cardBrand: ''
                }
            },
            errors: undefined
        }

        await mutate(optimisticCardData, false)

        try {
            const response = await graphqlFetch<
                Record<string, never>,
                DeleteCardOutput
            >({
                query: deleteCardQuery
            })

            if (response?.data?.deleteCard?.status !== 'true') {
                showGenericError()
                await mutate()
            }
        } catch {
            showGenericError()
            await mutate()
        }
    }, [mutate, showGenericError])

    const contextValue = React.useMemo(
        () => ({
            numberOfInstallments,
            setNumOfInstallments,
            installmentsArray,
            isLoading,
            isValidating,
            data,
            cardNumber,
            cardType,
            maxInstallments,
            error,
            mutate,
            handleCreateCard,
            handleRemoveCard,
            isCreateCardLoading,
            setIsCreateCardLoading,
            cardBrand
        }),
        [
            numberOfInstallments,
            installmentsArray,
            isLoading,
            isValidating,
            data,
            cardNumber,
            cardType,
            maxInstallments,
            error,
            mutate,
            handleCreateCard,
            handleRemoveCard,
            isCreateCardLoading,
            cardBrand
        ]
    )

    return (
        <CardInfoContext.Provider value={contextValue}>
            {children}
        </CardInfoContext.Provider>
    )
}

function useCardInfo(): CardInfoContextType {
    const context = React.useContext(CardInfoContext)

    if (!context) {
        throw new Error('useCardInfo must be used within a CardInfoProvider')
    }

    return context
}

export { CardInfoProvider, useCardInfo }
